import React, { useState, useEffect } from 'react';
import { Button as CarbonButton, TextInput, Layer } from '@carbon/react';
import { Close, TrashCan, Save } from '@carbon/icons-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { LuLoader, LuWifi, LuGlobe, LuKey, LuServer, LuCopy, LuCheck, LuRadio, LuWifiOff, LuBraces, LuTable2 } from 'react-icons/lu';
import { useEtlAuth as useAuth } from '../hooks/useEtlAuth';
import { ETL_API } from '../../../config/etlApi';
import { getGatewayMqttTopic } from '../utils/mqttDefaults';
import { findAccountGateway, gatewayStatusLabel, normalizeGatewayStatus } from '../utils/gatewayStatus';
import {
  resolveGatewayNodeData,
  previewMessagesForNode,
  getUpstreamGatewayMessages,
} from '../utils/pipelineUpstream';
import { toast } from 'sonner';
import LiveDataTable from './LiveDataTable';
import { AiNodeAssist } from './AiNodeAssist';

import {
  AI_PROVIDER_OPTIONS,
  AI_NODE_TYPES,
  getDefaultAiConfig,
  hasConfiguredAi,
  aiApiKeyPlaceholder,
} from '../../../config/aiConfig';

/**
 * GatewayDataView — renders the live MQTT payloads for a single gateway.
 * JSON view shows the raw payload shape; Table view flattens it into columns
 * so the user can see the data structure at a glance.
 */
const GatewayDataView = ({ messages }) => {
  const [mode, setMode] = useState('json'); // 'json' | 'table'

  return (
    <>
      <div className="config-field">
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <Button
            variant={mode === 'json' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('json')}
          >
            <LuBraces className="w-4 h-4 mr-1" /> JSON
          </Button>
          <Button
            variant={mode === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('table')}
          >
            <LuTable2 className="w-4 h-4 mr-1" /> Table
          </Button>
          <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: '0.75rem', opacity: 0.6 }}>
            {messages.length} message(s)
          </span>
        </div>
      </div>

      {mode === 'json' ? (
        <div style={{ maxHeight: '320px', overflow: 'auto', borderRadius: '8px', border: '1px solid var(--config-border, #e0e0e0)' }}>
          {messages.slice(0, 15).map((msg, i) => (
            <div key={i} style={{ borderBottom: '1px solid var(--config-border, #f0f0f0)', padding: '8px' }}>
              <div style={{ fontSize: '0.7rem', opacity: 0.6, marginBottom: 4 }}>
                {msg.topic} · {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
              </div>
              <pre style={{ margin: 0, fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(msg.payload, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ maxHeight: '320px', overflow: 'auto', borderRadius: '8px', border: '1px solid var(--config-border, #e0e0e0)' }}>
          <LiveDataTable messages={messages} />
        </div>
      )}
    </>
  );
};

/** Live MQTT preview for nodes downstream of a gateway (JSON Parse, Mapper, etc.). */
const UpstreamLivePreview = ({
  node,
  nodeType,
  config,
  nodes,
  edges,
  liveMessages,
  mqttConnected,
  previewLabel,
}) => {
  const { gatewayNode, label, messages, previewMessages, topic } = previewMessagesForNode(
    node?.id,
    nodeType,
    config,
    nodes,
    edges,
    liveMessages,
  );
  const displayMessages = previewMessages.length ? previewMessages : messages;
  const sourceLabel = label || 'gateway';
  const totalBuffered = (liveMessages || []).length;

  return (
    <>
      <div className="config-divider" />
      <div className="config-section-header">
        <LuRadio className="inline w-4 h-4 mr-2" />
        {previewLabel || 'Live data from upstream'}
      </div>
      <div className="config-field">
        <Label>Source</Label>
        <Input
          value={gatewayNode ? sourceLabel : 'Not connected to a gateway'}
          readOnly
          className="config-input opacity-80"
        />
        <span className="config-hint">
          {gatewayNode
            ? `Run the pipeline, then data from "${sourceLabel}" appears here.`
            : 'Wire this node to a Gateway (directly or through other nodes).'}
        </span>
      </div>
      {!mqttConnected ? (
        <div className="config-field">
          <div style={{
            padding: '24px 16px',
            textAlign: 'center',
            border: '1px dashed var(--config-border, #e0e0e0)',
            borderRadius: '8px',
            color: 'var(--config-text-secondary, #6f6f6f)',
          }}>
            <LuWifiOff className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>Click Run to start streaming MQTT data.</p>
          </div>
        </div>
      ) : displayMessages.length === 0 ? (
        <div className="config-field">
          <div style={{
            padding: '24px 16px',
            textAlign: 'center',
            border: '1px dashed var(--config-border, #e0e0e0)',
            borderRadius: '8px',
            color: 'var(--config-text-secondary, #6f6f6f)',
          }}>
            <LuRadio className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Waiting for data from &quot;{sourceLabel}&quot;…
            </p>
            {totalBuffered > 0 ? (
              <p style={{ margin: '8px 0 0', fontSize: '0.75rem' }}>
                WebSocket has {totalBuffered} message(s), but none matched topic{' '}
                <code>{topic || 'kbeacon/publish/{MAC}'}</code>. Check the gateway MAC on the canvas node.
              </p>
            ) : (
              <p style={{ margin: '8px 0 0', fontSize: '0.75rem' }}>
                Run is active but no MQTT reached the browser. Start local ETL on port 8080, set{' '}
                <code>REACT_APP_ETL_DEV_BACKEND=http://127.0.0.1:8080</code>, restart{' '}
                <code>yarn start</code> (WebSocket connects direct to :8080).
              </p>
            )}
          </div>
        </div>
      ) : (
        <GatewayDataView messages={displayMessages} />
      )}
    </>
  );
};

export const ConfigPanel = ({ node, onClose, onUpdate, onDelete, liveMessages = [], mqttConnected = false, nodes = [], edges = [] }) => {
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const { saveGateway, removeGateway, gateways, user } = useAuth();

  useEffect(() => {
    if (!node) return;

    let base = node.data || {};
    const nodeType = base.nodeType || (node.type === 'gateway' ? 'kGateway' : undefined);

    if (nodeType === 'kGateway' || node.type === 'gateway') {
      base = resolveGatewayNodeData(base, gateways);
    }

    if (AI_NODE_TYPES.has(nodeType) && !base.aiProvider) {
      setConfig({ ...getDefaultAiConfig(), ...base });
    } else {
      setConfig(base);
    }
  }, [node, gateways]);

  // On-demand MQTT subscription for gateway data preview.
  // When a gateway node (or a View node whose upstream is a gateway) is selected
  // and the broker is connected, subscribe to that gateway's publish topic so the
  // WebSocket actually receives its payloads. Unsubscribe on cleanup.
  useEffect(() => {
    if (!mqttConnected || !node) return undefined;

    // Resolve the topic to subscribe to.
    let topic = '';
    const nt = config.nodeType || node.data?.nodeType;
    if (nt === 'kGateway' || node.type === 'gateway') {
      topic = getGatewayMqttTopic(config);
    } else if (nt === 'viewNode') {
      // For a View node, subscribe to its upstream gateway's topic if applicable.
      const incoming = (edges || []).filter((e) => e.target === node.id);
      const upstream = incoming.length > 0 ? (nodes || []).find((n) => n.id === incoming[0].source) : null;
      const isGateway = upstream && (upstream.type === 'gateway' || upstream.data?.nodeType === 'kGateway');
      if (isGateway) {
        topic = getGatewayMqttTopic(upstream.data || {});
      }
    }

    if (!topic) return undefined;

    let cancelled = false;
    const apiBase = ETL_API; // e.g. '/api' or 'https://.../api'

    (async () => {
      try {
        const res = await fetch(`${apiBase}/mqtt/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ topic, qos: 0 }),
        });
        if (!cancelled && res.ok) {
          // eslint-disable-next-line no-console
          console.log(`[ConfigPanel] Subscribed to gateway topic: ${topic}`);
        }
      } catch (e) {
        // Non-fatal — data tab will just show "waiting".
        console.warn('[ConfigPanel] Subscribe failed:', e);
      }
    })();

    return () => {
      cancelled = true;
      fetch(`${apiBase}/mqtt/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic }),
      }).catch(() => { /* ignore cleanup errors */ });
    };
  }, [mqttConnected, node, config.mac_address, config.mqtt_topic, config.nodeType, nodes, edges]);

  if (!node) return null;

  const handleChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
  };

  const handleSave = () => {
    if (onUpdate) {
      onUpdate(node.id, config);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(node.id);
    }
    onClose();
  };

  const nodeType = config.nodeType || 'unknown';
  const aiConfig = { ...getDefaultAiConfig(), ...config };

  const renderFields = () => {
    switch (nodeType) {
      case 'mqttInput':
        return (
          <>
            <div className="config-field">
              <Label>Topic</Label>
              <Input
                value={config.topic || ''}
                onChange={(e) => handleChange('topic', e.target.value)}
                placeholder="sensors/+/temperature"
                className="config-input"
                data-testid="config-topic"
              />
              <span className="config-hint">Wildcards: + (single level), # (multi level)</span>
            </div>
            <div className="config-field">
              <Label>QoS</Label>
              <Select value={String(config.qos || 0)} onValueChange={(v) => handleChange('qos', parseInt(v))}>
                <SelectTrigger className="config-input" data-testid="config-qos">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 - At most once</SelectItem>
                  <SelectItem value="1">1 - At least once</SelectItem>
                  <SelectItem value="2">2 - Exactly once</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case 'httpInput':
        return (
          <>
            <div className="config-section-header">Source Mode</div>
            <div className="config-field">
              <Label>Mode</Label>
              <Select value={config.mode || 'poll'} onValueChange={(v) => handleChange('mode', v)}>
                <SelectTrigger className="config-input" data-testid="config-http-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="poll">Poll Endpoint</SelectItem>
                  <SelectItem value="webhook">Webhook Receiver</SelectItem>
                </SelectContent>
              </Select>
              <span className="config-hint">
                {config.mode === 'webhook' 
                  ? 'Receives incoming HTTP POST requests' 
                  : 'Fetches data from URL on schedule'}
              </span>
            </div>

            {config.mode !== 'webhook' && (
              <>
                <div className="config-divider" />
                <div className="config-section-header">Endpoint</div>
                <div className="config-field">
                  <Label>URL</Label>
                  <Input
                    value={config.url || ''}
                    onChange={(e) => handleChange('url', e.target.value)}
                    placeholder="https://api.example.com/data"
                    className="config-input"
                    data-testid="config-http-url"
                  />
                </div>
                <div className="config-field">
                  <Label>Method</Label>
                  <Select value={config.method || 'GET'} onValueChange={(v) => handleChange('method', v)}>
                    <SelectTrigger className="config-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="config-field">
                  <Label>Poll Interval (seconds)</Label>
                  <Input
                    type="number"
                    value={config.interval || 30}
                    onChange={(e) => handleChange('interval', parseInt(e.target.value) || 30)}
                    min="5"
                    className="config-input"
                  />
                </div>
              </>
            )}

            {config.mode === 'webhook' && (
              <>
                <div className="config-divider" />
                <div className="config-section-header">Webhook Settings</div>
                <div className="config-field">
                  <Label>Webhook Path</Label>
                  <Input
                    value={config.webhookPath || ''}
                    onChange={(e) => handleChange('webhookPath', e.target.value)}
                    placeholder="/webhook/my-endpoint"
                    className="config-input"
                    data-testid="config-webhook-path"
                  />
                  <span className="config-hint">Unique path for this webhook receiver</span>
                </div>
              </>
            )}

            <div className="config-divider" />
            <div className="config-section-header">Authentication</div>
            <div className="config-field">
              <Label>Auth Type</Label>
              <Select value={config.authType || 'none'} onValueChange={(v) => handleChange('authType', v)}>
                <SelectTrigger className="config-input" data-testid="config-auth-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="basic">Basic Auth (Username/Password)</SelectItem>
                  <SelectItem value="bearer">Bearer Token</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {config.authType === 'basic' && (
              <>
                <div className="config-field">
                  <Label>Username</Label>
                  <Input
                    value={config.authUsername || ''}
                    onChange={(e) => handleChange('authUsername', e.target.value)}
                    placeholder="username"
                    className="config-input"
                    data-testid="config-auth-username"
                  />
                </div>
                <div className="config-field">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={config.authPassword || ''}
                    onChange={(e) => handleChange('authPassword', e.target.value)}
                    placeholder="password"
                    className="config-input"
                    data-testid="config-auth-password"
                  />
                </div>
              </>
            )}

            {config.authType === 'bearer' && (
              <div className="config-field">
                <Label>Bearer Token</Label>
                <Input
                  type="password"
                  value={config.bearerToken || ''}
                  onChange={(e) => handleChange('bearerToken', e.target.value)}
                  placeholder="eyJhbGciOiJIUzI1NiIs..."
                  className="config-input"
                  data-testid="config-bearer-token"
                />
                <span className="config-hint">Token will be sent as: Authorization: Bearer [token]</span>
              </div>
            )}

            {config.mode !== 'webhook' && (
              <>
                <div className="config-divider" />
                <div className="config-section-header">Request Body (for POST)</div>
                <div className="config-field">
                  <Textarea
                    value={config.requestBody || ''}
                    onChange={(e) => handleChange('requestBody', e.target.value)}
                    placeholder='{"query": "latest"}'
                    className="config-input config-textarea"
                  />
                </div>
              </>
            )}
          </>
        );

      case 'mqttOutput':
        return (
          <>
            <div className="config-field">
              <Label>Topic</Label>
              <Input
                value={config.topic || ''}
                onChange={(e) => handleChange('topic', e.target.value)}
                placeholder="output/processed"
                className="config-input"
                data-testid="config-topic"
              />
            </div>
            <div className="config-field">
              <Label>QoS</Label>
              <Select value={String(config.qos || 0)} onValueChange={(v) => handleChange('qos', parseInt(v))}>
                <SelectTrigger className="config-input" data-testid="config-qos">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 - At most once</SelectItem>
                  <SelectItem value="1">1 - At least once</SelectItem>
                  <SelectItem value="2">2 - Exactly once</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-field-row">
              <Label>Retain Message</Label>
              <Switch checked={config.retain || false} onCheckedChange={(v) => handleChange('retain', v)} />
            </div>
          </>
        );

      case 'filter':
        return (
          <>
            <div className="config-field">
              <Label>Filter Condition</Label>
              <Textarea
                value={config.condition || ''}
                onChange={(e) => handleChange('condition', e.target.value)}
                placeholder="payload.rssi > -70 and payload.type == 32"
                className="config-input config-textarea"
                data-testid="config-condition"
              />
              <span className="config-hint">
                Python expression evaluated per message. Variables: payload, topic, msg.
                Message is dropped when the expression is false.
              </span>
            </div>
            <div className="config-field">
              <Label>Example Conditions</Label>
              <div className="config-examples">
                <code onClick={() => handleChange('condition', "payload.msg == 'advData'")}>payload.msg == &apos;advData&apos;</code>
                <code onClick={() => handleChange('condition', 'payload.rssi > -70')}>payload.rssi &gt; -70</code>
                <code onClick={() => handleChange('condition', 'len(payload) > 0')}>len(payload) &gt; 0</code>
              </div>
            </div>
            <UpstreamLivePreview
              node={node}
              nodeType="filter"
              config={config}
              nodes={nodes}
              edges={edges}
              liveMessages={liveMessages}
              mqttConnected={mqttConnected}
              previewLabel="Incoming data (upstream)"
            />
          </>
        );

      case 'switch':
        return (
          <>
            <div className="config-field">
              <Label>Property to Switch On</Label>
              <Input
                value={config.property || ''}
                onChange={(e) => handleChange('property', e.target.value)}
                placeholder="payload.type"
                className="config-input"
                data-testid="config-property"
              />
            </div>
            <div className="config-field">
              <Label>Cases (one per line)</Label>
              <Textarea
                value={config.cases || ''}
                onChange={(e) => handleChange('cases', e.target.value)}
                placeholder="temperature&#10;humidity&#10;default"
                className="config-input config-textarea"
                data-testid="config-cases"
              />
            </div>
          </>
        );

      case 'throttle':
        return (
          <>
            <div className="config-field">
              <Label>Rate Limit</Label>
              <div className="config-row">
                <Input
                  type="number"
                  value={config.rate || 1}
                  onChange={(e) => handleChange('rate', parseInt(e.target.value))}
                  className="config-input"
                  style={{ width: '80px' }}
                  data-testid="config-rate"
                />
                <span className="config-label-inline">messages per</span>
                <Select value={config.unit || 'second'} onValueChange={(v) => handleChange('unit', v)}>
                  <SelectTrigger className="config-input" style={{ width: '100px' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="second">Second</SelectItem>
                    <SelectItem value="minute">Minute</SelectItem>
                    <SelectItem value="hour">Hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="config-field">
              <Label>Mode</Label>
              <Select value={config.mode || 'drop'} onValueChange={(v) => handleChange('mode', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="drop">Drop excess messages</SelectItem>
                  <SelectItem value="queue">Queue excess messages</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case 'dedupe':
        return (
          <>
            <div className="config-field">
              <Label>Dedupe Key</Label>
              <Input
                value={config.dedupeKey || ''}
                onChange={(e) => handleChange('dedupeKey', e.target.value)}
                placeholder="payload.id"
                className="config-input"
              />
              <span className="config-hint">Property path to use for deduplication</span>
            </div>
            <div className="config-field">
              <Label>Time Window (seconds)</Label>
              <Input
                type="number"
                value={config.windowSize || 60}
                onChange={(e) => handleChange('windowSize', parseInt(e.target.value))}
                className="config-input"
              />
            </div>
          </>
        );

      case 'join':
        return (
          <>
            <div className="config-field">
              <Label>Join Mode</Label>
              <Select value={config.joinMode || 'merge'} onValueChange={(v) => handleChange('joinMode', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">Merge objects</SelectItem>
                  <SelectItem value="array">Collect as array</SelectItem>
                  <SelectItem value="zip">Zip by timestamp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-field">
              <Label>Join Key</Label>
              <Input
                value={config.joinKey || ''}
                onChange={(e) => handleChange('joinKey', e.target.value)}
                placeholder="timestamp"
                className="config-input"
              />
            </div>
            <div className="config-field">
              <Label>Window (seconds)</Label>
              <Input
                type="number"
                value={config.joinWindow || 5}
                onChange={(e) => handleChange('joinWindow', parseInt(e.target.value))}
                className="config-input"
              />
            </div>
          </>
        );

      case 'jsonParser':
        return (
          <>
            <div className="config-field">
              <Label>JSON Path</Label>
              <Input
                value={config.jsonPath || ''}
                onChange={(e) => handleChange('jsonPath', e.target.value)}
                placeholder="$.obj"
                className="config-input"
              />
              <span className="config-hint">JSONPath expression — gateway payloads often use $.obj for the device array</span>
            </div>
            <UpstreamLivePreview
              node={node}
              nodeType="jsonParser"
              config={config}
              nodes={nodes}
              edges={edges}
              liveMessages={liveMessages}
              mqttConnected={mqttConnected}
              previewLabel="Parsed preview (JSON Path applied)"
            />
          </>
        );

      case 'mapper':
        return (
          <>
            <div className="config-field">
              <Label>Field Mappings</Label>
              <Textarea
                value={config.mappings || ''}
                onChange={(e) => handleChange('mappings', e.target.value)}
                placeholder="oldKey:newKey&#10;temp:temperature&#10;ts:timestamp"
                className="config-input config-textarea"
              />
              <span className="config-hint">One mapping per line: sourceField:targetField</span>
            </div>
            <UpstreamLivePreview
              node={node}
              nodeType="mapper"
              config={config}
              nodes={nodes}
              edges={edges}
              liveMessages={liveMessages}
              mqttConnected={mqttConnected}
              previewLabel="Input preview (after upstream JSON Parse if wired)"
            />
          </>
        );

      case 'function':
        return (
          <>
            <div className="config-field">
              <Label>Transform Function</Label>
              <Textarea
                value={config.code || ''}
                onChange={(e) => handleChange('code', e.target.value)}
                placeholder="// Transform the message&#10;// Available: msg, payload, topic&#10;&#10;// Example: Parse dB value&#10;if (typeof payload === 'string' && payload.includes('dB')) {&#10;  const value = parseFloat(payload);&#10;  return { value, unit: 'dB', raw: payload };&#10;}&#10;return payload;"
                className="config-input config-textarea-lg"
                data-testid="config-function"
              />
              <span className="config-hint">Return the transformed payload</span>
            </div>
            <div className="config-field">
              <Label>Example Transforms</Label>
              <div className="config-examples">
                <code onClick={() => handleChange('code', "// Parse numeric value\nconst value = parseFloat(payload);\nreturn { value, raw: payload };")}>Parse number</code>
                <code onClick={() => handleChange('code', "// Add timestamp\nreturn { ...payload, processed_at: new Date().toISOString() };")}>Add timestamp</code>
                <code onClick={() => handleChange('code', "// Extract device ID from topic\nconst device = topic.split('/')[1];\nreturn { device, data: payload };")}>Extract from topic</code>
              </div>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">AI Code Assistant</div>
            <div className="config-field">
              <Label>LLM Provider</Label>
              <Select value={config.aiProvider || ''} onValueChange={(v) => handleChange('aiProvider', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue placeholder="Select provider..." />
                </SelectTrigger>
                <SelectContent>
                  {AI_PROVIDER_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {config.aiProvider && (
              <>
                <div className="config-field">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={config.aiApiKey || ''}
                    onChange={(e) => handleChange('aiApiKey', e.target.value)}
                    placeholder={aiApiKeyPlaceholder(config.aiProvider)}
                    className="config-input"
                    data-testid="config-ai-key"
                  />
                </div>
                <div className="config-field">
                  <Label>Describe what you want</Label>
                  <Textarea
                    value={config.aiPrompt || ''}
                    onChange={(e) => handleChange('aiPrompt', e.target.value)}
                    placeholder="Convert Celsius to Fahrenheit and add alert if temp > 85°F"
                    className="config-input config-textarea"
                    data-testid="config-ai-prompt"
                  />
                </div>
                <Button 
                  className="ai-generate-btn"
                  onClick={() => {
                    // AI generation will be handled by parent component
                    if (window.generateAICode) {
                      window.generateAICode(node.id, config);
                    }
                  }}
                  data-testid="ai-generate-btn"
                >
                  <span className="ai-sparkle">✨</span> Generate Code
                </Button>
              </>
            )}
          </>
        );

      case 'bleDecoder':
        return (
          <>
            <div className="config-section-header">Input Format</div>
            <div className="config-field">
              <Label>Payload Format</Label>
              <Select value={config.inputFormat || 'auto'} onValueChange={(v) => handleChange('inputFormat', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  <SelectItem value="hex">Hex String (1A2B3C)</SelectItem>
                  <SelectItem value="hex_prefix">Hex with 0x (0x1A2B3C)</SelectItem>
                  <SelectItem value="base64">Base64 Encoded</SelectItem>
                  <SelectItem value="array">Byte Array [26, 43, 60]</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Byte Mappings</div>
            <div className="config-field">
              <Label>Field Definitions</Label>
              <Textarea
                value={config.mappings || ''}
                onChange={(e) => handleChange('mappings', e.target.value)}
                placeholder="# Format: start-end:name:type:transform&#10;# Types: int8, uint8, int16le, int16be, uint16le, uint16be, int32le, float32le&#10;&#10;0-1:temperature:int16le:/100&#10;2-3:humidity:uint16le:/100&#10;4:battery:uint8:*1&#10;5:status:uint8:enum:0=OK,1=LOW_BATTERY,2=ERROR"
                className="config-input config-textarea-lg"
                data-testid="config-ble-mappings"
              />
              <span className="config-hint">Define byte ranges and their transformations</span>
            </div>
            <div className="config-field">
              <Label>Quick Templates</Label>
              <div className="config-examples">
                <code onClick={() => handleChange('mappings', "0-1:temperature:int16le:/100\n2-3:humidity:uint16le:/100\n4:battery:uint8:*1")}>Temp/Humidity Sensor</code>
                <code onClick={() => handleChange('mappings', "0-3:value:float32le:*1\n4:unit:uint8:enum:0=C,1=F,2=K")}>Float Value + Unit</code>
                <code onClick={() => handleChange('mappings', "0:flags:uint8:bits:0=motion,1=door,2=window,3=alarm")}>Bit Flags</code>
              </div>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Options</div>
            <div className="config-field-row">
              <Label>Preserve raw payload as "_raw"</Label>
              <Switch 
                checked={config.preserveRaw !== false} 
                onCheckedChange={(v) => handleChange('preserveRaw', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Skip invalid payloads</Label>
              <Switch 
                checked={config.skipInvalid || false} 
                onCheckedChange={(v) => handleChange('skipInvalid', v)} 
              />
            </div>
          </>
        );

      case 'aiTransform':
        return (
          <>
            <div className="config-section-header">AI Configuration</div>
            <div className="config-field">
              <Label>LLM Provider</Label>
              <Select value={config.aiProvider || ''} onValueChange={(v) => handleChange('aiProvider', v)}>
                <SelectTrigger className="config-input" data-testid="config-ai-provider">
                  <SelectValue placeholder="Select provider..." />
                </SelectTrigger>
                <SelectContent>
                  {AI_PROVIDER_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {config.aiProvider && (
              <div className="config-field">
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={config.aiApiKey || ''}
                  onChange={(e) => handleChange('aiApiKey', e.target.value)}
                  placeholder={aiApiKeyPlaceholder(config.aiProvider)}
                  className="config-input"
                  data-testid="config-ai-api-key"
                />
              </div>
            )}
            <div className="config-divider" />
            <div className="config-section-header">Transform Mode</div>
            <div className="config-field">
              <Label>Mode</Label>
              <Select value={config.mode || 'ble'} onValueChange={(v) => handleChange('mode', v)}>
                <SelectTrigger className="config-input" data-testid="config-ai-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ble">BLE Payload Interpreter</SelectItem>
                  <SelectItem value="schema">Schema Generator (SQL)</SelectItem>
                  <SelectItem value="migration">Migration Recommender</SelectItem>
                  <SelectItem value="custom">Custom Transform</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(config.mode || 'ble') === 'ble' && (
              <>
                <div className="config-field">
                  <Label>Manufacturer Documentation</Label>
                  <Textarea
                    value={config.manufacturerDocs || ''}
                    onChange={(e) => handleChange('manufacturerDocs', e.target.value)}
                    placeholder="Paste manufacturer documentation or describe the BLE payload format...&#10;&#10;Example: 'Bytes 0-1 contain temperature as signed 16-bit little-endian in 0.01°C units. Byte 2 is humidity percentage. Byte 3 is battery level 0-100.'"
                    className="config-input config-textarea-lg"
                    data-testid="config-manufacturer-docs"
                  />
                </div>
                <div className="config-field">
                  <Label>Example Payload (optional)</Label>
                  <Input
                    value={config.examplePayload || ''}
                    onChange={(e) => handleChange('examplePayload', e.target.value)}
                    placeholder="1A2B3C4D or base64..."
                    className="config-input"
                  />
                  <span className="config-hint">Provide example for better accuracy</span>
                </div>
                <div className="config-field">
                  <Label>Expected Output (optional)</Label>
                  <Input
                    value={config.expectedOutput || ''}
                    onChange={(e) => handleChange('expectedOutput', e.target.value)}
                    placeholder='{"temperature": 23.5, "humidity": 60}'
                    className="config-input"
                  />
                </div>
              </>
            )}
            
            {(config.mode || 'ble') === 'schema' && (
              <>
                <div className="config-field">
                  <Label>Target Database</Label>
                  <Select value={config.targetDb || 'postgresql'} onValueChange={(v) => handleChange('targetDb', v)}>
                    <SelectTrigger className="config-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="postgresql">PostgreSQL</SelectItem>
                      <SelectItem value="mysql">MySQL</SelectItem>
                      <SelectItem value="mongodb">MongoDB</SelectItem>
                      <SelectItem value="sqlite">SQLite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="config-field">
                  <Label>Table Name</Label>
                  <Input
                    value={config.tableName || ''}
                    onChange={(e) => handleChange('tableName', e.target.value)}
                    placeholder="sensor_data"
                    className="config-input"
                  />
                </div>
              </>
            )}
            
            {(config.mode || 'ble') === 'migration' && (
              <>
                <div className="config-field">
                  <Label>Current Schema (optional)</Label>
                  <Textarea
                    value={config.currentSchema || ''}
                    onChange={(e) => handleChange('currentSchema', e.target.value)}
                    placeholder="Paste existing CREATE TABLE statement or leave blank for new table..."
                    className="config-input config-textarea"
                  />
                </div>
              </>
            )}
            
            {(config.mode || 'ble') === 'custom' && (
              <>
                <div className="config-field">
                  <Label>Transform Instructions</Label>
                  <Textarea
                    value={config.customInstructions || ''}
                    onChange={(e) => handleChange('customInstructions', e.target.value)}
                    placeholder="Describe how you want to transform the data...&#10;&#10;Example: 'Convert all temperature values from Celsius to Fahrenheit, add a severity level based on value ranges, and flatten nested location objects.'"
                    className="config-input config-textarea-lg"
                  />
                </div>
              </>
            )}
            
            <div className="config-divider" />
            <Button 
              className="ai-generate-btn full-width"
              disabled={!hasConfiguredAi(config)}
              onClick={() => {
                if (window.generateAITransform) {
                  window.generateAITransform(node.id, config);
                }
              }}
              data-testid="ai-transform-btn"
            >
              <span className="ai-sparkle">🧠</span> 
              {config.mode === 'ble' ? 'Generate Decoder Rules' : 
               config.mode === 'schema' ? 'Generate SQL Schema' :
               config.mode === 'migration' ? 'Generate Migration' : 'Generate Transform'}
            </Button>
            
            {config.generatedRules && (
              <div className="config-field">
                <Label>Generated Rules</Label>
                <Textarea
                  value={config.generatedRules || ''}
                  onChange={(e) => handleChange('generatedRules', e.target.value)}
                  className="config-input config-textarea-lg ai-output"
                  data-testid="config-generated-rules"
                />
                <span className="config-hint">You can edit the generated rules</span>
              </div>
            )}
          </>
        );

      case 'bleElaPtEn':
        return (
          <>
            <div className="config-section-header">ELA P T EN Decoder</div>
            <div className="config-info-box">
              <p>Decodes ELA TAG P T EN temperature sensors</p>
              <p className="text-xs text-zinc-500 mt-1">Filters: type=32/64, manufacturer=0x5707</p>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Input Configuration</div>
            <div className="config-field">
              <Label>Array Path</Label>
              <Input
                value={config.arrayPath || 'obj'}
                onChange={(e) => handleChange('arrayPath', e.target.value)}
                placeholder="obj"
                className="config-input"
                data-testid="config-array-path"
              />
              <span className="config-hint">Path to device array in payload (e.g., "obj")</span>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Output Options</div>
            <div className="config-field-row">
              <Label>Include gateway MAC</Label>
              <Switch 
                checked={config.includeGateway !== false} 
                onCheckedChange={(v) => handleChange('includeGateway', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include RSSI</Label>
              <Switch 
                checked={config.includeRssi !== false} 
                onCheckedChange={(v) => handleChange('includeRssi', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include raw advertisement</Label>
              <Switch 
                checked={config.includeRaw || false} 
                onCheckedChange={(v) => handleChange('includeRaw', v)} 
              />
            </div>
            <div className="config-field">
              <Label>Output Mode</Label>
              <Select value={config.outputMode || 'each'} onValueChange={(v) => handleChange('outputMode', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="each">Each device as separate message</SelectItem>
                  <SelectItem value="array">All devices as array</SelectItem>
                  <SelectItem value="first">First matched device only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">AI Assist (Optional)</div>
            <div className="config-field">
              <Label>LLM Provider</Label>
              <Select value={config.aiProvider || ''} onValueChange={(v) => handleChange('aiProvider', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue placeholder="Select to enable AI..." />
                </SelectTrigger>
                <SelectContent>
                  {AI_PROVIDER_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {config.aiProvider && (
              <>
                <div className="config-field">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={config.aiApiKey || ''}
                    onChange={(e) => handleChange('aiApiKey', e.target.value)}
                    placeholder="Enter API key..."
                    className="config-input"
                  />
                </div>
                <div className="config-field">
                  <Label>Sample Payload (hex)</Label>
                  <Input
                    value={config.samplePayload || ''}
                    onChange={(e) => handleChange('samplePayload', e.target.value)}
                    placeholder="02010606FF570712C9..."
                    className="config-input"
                  />
                </div>
                <Button 
                  className="ai-generate-btn"
                  disabled={!hasConfiguredAi(config) || !config.samplePayload}
                  onClick={() => {
                    if (window.analyzeBleSample) {
                      window.analyzeBleSample(node.id, config, 'ela_pten');
                    }
                  }}
                >
                  <span className="ai-sparkle">✨</span> Analyze & Refine Decoder
                </Button>
              </>
            )}
          </>
        );

      case 'bleAutoDetect':
        return (
          <>
            <div className="config-section-header">BLE Auto-Detect</div>
            <div className="config-info-box">
              <p>AI automatically identifies device type and decodes payload</p>
              <p className="text-xs text-zinc-500 mt-1">Supports: ELA, KBPro, SigmaWit, and unknown devices</p>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">AI Configuration</div>
            <div className="config-field">
              <Label>LLM Provider</Label>
              <Select value={config.aiProvider || ''} onValueChange={(v) => handleChange('aiProvider', v)}>
                <SelectTrigger className="config-input" data-testid="config-ai-provider">
                  <SelectValue placeholder="Select provider..." />
                </SelectTrigger>
                <SelectContent>
                  {AI_PROVIDER_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {config.aiProvider && (
              <div className="config-field">
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={config.aiApiKey || ''}
                  onChange={(e) => handleChange('aiApiKey', e.target.value)}
                  placeholder={aiApiKeyPlaceholder(config.aiProvider)}
                  className="config-input"
                  data-testid="config-ai-api-key"
                />
              </div>
            )}
            <div className="config-divider" />
            <div className="config-section-header">Input Configuration</div>
            <div className="config-field">
              <Label>Array Path</Label>
              <Input
                value={config.arrayPath || 'obj'}
                onChange={(e) => handleChange('arrayPath', e.target.value)}
                placeholder="obj"
                className="config-input"
              />
              <span className="config-hint">Path to device array in payload</span>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Known Device Profiles</div>
            <div className="config-field-row">
              <Label>Enable KBPro (type=1)</Label>
              <Switch 
                checked={config.enableKbPro !== false} 
                onCheckedChange={(v) => handleChange('enableKbPro', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Enable ELA P T EN (0x5707)</Label>
              <Switch 
                checked={config.enableEla !== false} 
                onCheckedChange={(v) => handleChange('enableEla', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Enable SigmaWit (A4C138)</Label>
              <Switch 
                checked={config.enableSigmaWit !== false} 
                onCheckedChange={(v) => handleChange('enableSigmaWit', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Use AI for unknown devices</Label>
              <Switch 
                checked={config.aiForUnknown !== false} 
                onCheckedChange={(v) => handleChange('aiForUnknown', v)} 
              />
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Output Options</div>
            <div className="config-field-row">
              <Label>Include device_type in output</Label>
              <Switch 
                checked={config.includeDeviceType !== false} 
                onCheckedChange={(v) => handleChange('includeDeviceType', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include gateway MAC</Label>
              <Switch 
                checked={config.includeGateway !== false} 
                onCheckedChange={(v) => handleChange('includeGateway', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include RSSI</Label>
              <Switch 
                checked={config.includeRssi !== false} 
                onCheckedChange={(v) => handleChange('includeRssi', v)} 
              />
            </div>
            <div className="config-field">
              <Label>Output Mode</Label>
              <Select value={config.outputMode || 'each'} onValueChange={(v) => handleChange('outputMode', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="each">Each device as separate message</SelectItem>
                  <SelectItem value="array">All devices as array</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case 'bleKbPro':
        return (
          <>
            <div className="config-section-header">KBPro Decoder</div>
            <div className="config-info-box">
              <p>Decodes KBPro sensors (temp, humidity, accelerometer)</p>
              <p className="text-xs text-zinc-500 mt-1">Filters: type=1 (pre-parsed by gateway)</p>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Input Configuration</div>
            <div className="config-field">
              <Label>Array Path</Label>
              <Input
                value={config.arrayPath || 'obj'}
                onChange={(e) => handleChange('arrayPath', e.target.value)}
                placeholder="obj"
                className="config-input"
                data-testid="config-array-path"
              />
              <span className="config-hint">Path to device array in payload (e.g., "obj")</span>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Data Selection</div>
            <div className="config-field-row">
              <Label>Include temperature</Label>
              <Switch 
                checked={config.includeTemp !== false} 
                onCheckedChange={(v) => handleChange('includeTemp', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include humidity</Label>
              <Switch 
                checked={config.includeHumidity !== false} 
                onCheckedChange={(v) => handleChange('includeHumidity', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include battery (mV)</Label>
              <Switch 
                checked={config.includeBattery !== false} 
                onCheckedChange={(v) => handleChange('includeBattery', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include accelerometer (x, y, z)</Label>
              <Switch 
                checked={config.includeAccel || false} 
                onCheckedChange={(v) => handleChange('includeAccel', v)} 
              />
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Output Options</div>
            <div className="config-field-row">
              <Label>Include gateway MAC</Label>
              <Switch 
                checked={config.includeGateway !== false} 
                onCheckedChange={(v) => handleChange('includeGateway', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include RSSI</Label>
              <Switch 
                checked={config.includeRssi !== false} 
                onCheckedChange={(v) => handleChange('includeRssi', v)} 
              />
            </div>
            <div className="config-field">
              <Label>Output Mode</Label>
              <Select value={config.outputMode || 'each'} onValueChange={(v) => handleChange('outputMode', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="each">Each device as separate message</SelectItem>
                  <SelectItem value="array">All devices as array</SelectItem>
                  <SelectItem value="first">First matched device only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">AI Assist (Optional)</div>
            <div className="config-field">
              <Label>LLM Provider</Label>
              <Select value={config.aiProvider || ''} onValueChange={(v) => handleChange('aiProvider', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue placeholder="Select to enable AI..." />
                </SelectTrigger>
                <SelectContent>
                  {AI_PROVIDER_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {config.aiProvider && (
              <>
                <div className="config-field">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={config.aiApiKey || ''}
                    onChange={(e) => handleChange('aiApiKey', e.target.value)}
                    placeholder="Enter API key..."
                    className="config-input"
                  />
                </div>
                <div className="config-field">
                  <Label>Sample Gateway Payload</Label>
                  <Textarea
                    value={config.samplePayload || ''}
                    onChange={(e) => handleChange('samplePayload', e.target.value)}
                    placeholder='{"type":1,"temp":23.5,"humidty":62.1,...}'
                    className="config-input config-textarea"
                  />
                </div>
                <Button 
                  className="ai-generate-btn"
                  disabled={!hasConfiguredAi(config) || !config.samplePayload}
                  onClick={() => {
                    if (window.analyzeBleSample) {
                      window.analyzeBleSample(node.id, config, 'kbpro');
                    }
                  }}
                >
                  <span className="ai-sparkle">✨</span> Analyze & Refine Decoder
                </Button>
              </>
            )}
          </>
        );

      case 'bleSigmaWit':
        return (
          <>
            <div className="config-section-header">SigmaWit T201 Decoder</div>
            <div className="config-info-box">
              <p>Decodes SigmaWit T201 temperature/humidity sensors</p>
              <p className="text-xs text-zinc-500 mt-1">Filters: MAC starts with A4C138, data contains 55AA</p>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Input Configuration</div>
            <div className="config-field">
              <Label>Array Path</Label>
              <Input
                value={config.arrayPath || 'obj'}
                onChange={(e) => handleChange('arrayPath', e.target.value)}
                placeholder="obj"
                className="config-input"
                data-testid="config-array-path"
              />
              <span className="config-hint">Path to device array in payload (e.g., "obj")</span>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Decoder Settings</div>
            <div className="config-field">
              <Label>Temperature Unit</Label>
              <Select value={config.tempUnit || 'celsius'} onValueChange={(v) => handleChange('tempUnit', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="celsius">Celsius (°C)</SelectItem>
                  <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Output Options</div>
            <div className="config-field-row">
              <Label>Include gateway MAC</Label>
              <Switch 
                checked={config.includeGateway !== false} 
                onCheckedChange={(v) => handleChange('includeGateway', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include RSSI</Label>
              <Switch 
                checked={config.includeRssi !== false} 
                onCheckedChange={(v) => handleChange('includeRssi', v)} 
              />
            </div>
            <div className="config-field-row">
              <Label>Include raw advertisement</Label>
              <Switch 
                checked={config.includeRaw || false} 
                onCheckedChange={(v) => handleChange('includeRaw', v)} 
              />
            </div>
            <div className="config-field">
              <Label>Output Mode</Label>
              <Select value={config.outputMode || 'each'} onValueChange={(v) => handleChange('outputMode', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="each">Each device as separate message</SelectItem>
                  <SelectItem value="array">All devices as array</SelectItem>
                  <SelectItem value="first">First matched device only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">AI Assist (Optional)</div>
            <div className="config-field">
              <Label>LLM Provider</Label>
              <Select value={config.aiProvider || ''} onValueChange={(v) => handleChange('aiProvider', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue placeholder="Select to enable AI..." />
                </SelectTrigger>
                <SelectContent>
                  {AI_PROVIDER_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {config.aiProvider && (
              <>
                <div className="config-field">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={config.aiApiKey || ''}
                    onChange={(e) => handleChange('aiApiKey', e.target.value)}
                    placeholder="Enter API key..."
                    className="config-input"
                  />
                </div>
                <div className="config-field">
                  <Label>Sample Payload (hex)</Label>
                  <Input
                    value={config.samplePayload || ''}
                    onChange={(e) => handleChange('samplePayload', e.target.value)}
                    placeholder="05095432303102010614FF55AA..."
                    className="config-input"
                  />
                </div>
                <Button 
                  className="ai-generate-btn"
                  disabled={!hasConfiguredAi(config) || !config.samplePayload}
                  onClick={() => {
                    if (window.analyzeBleSample) {
                      window.analyzeBleSample(node.id, config, 'sigmawit');
                    }
                  }}
                >
                  <span className="ai-sparkle">✨</span> Analyze & Refine Decoder
                </Button>
              </>
            )}
          </>
        );

      case 'redisSink':
        return (
          <>
            <div className="config-section-header">Connection</div>
            <div className="config-field">
              <Label>Redis URL</Label>
              <Input
                value={config.redisUrl || ''}
                onChange={(e) => handleChange('redisUrl', e.target.value)}
                placeholder="redis://default:password@host:6379"
                className="config-input"
                data-testid="config-redis-url"
              />
              <span className="config-hint">Full connection URL (Upstash format supported)</span>
            </div>
            <div className="config-field-row">
              <Label>Use TLS</Label>
              <Switch 
                checked={config.useTLS || false} 
                onCheckedChange={(v) => handleChange('useTLS', v)} 
                data-testid="config-redis-tls"
              />
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Data Settings</div>
            <div className="config-field">
              <Label>Key Pattern</Label>
              <Input
                value={config.keyPattern || ''}
                onChange={(e) => handleChange('keyPattern', e.target.value)}
                placeholder="iot:{device}:{type}"
                className="config-input"
                data-testid="config-key-pattern"
              />
              <span className="config-hint">Placeholders: {'{topic}'}, {'{device}'}, {'{timestamp}'}</span>
            </div>
            <div className="config-field">
              <Label>Operation</Label>
              <Select value={config.operation || 'SET'} onValueChange={(v) => handleChange('operation', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SET">SET (overwrite)</SelectItem>
                  <SelectItem value="HSET">HSET (hash field)</SelectItem>
                  <SelectItem value="LPUSH">LPUSH (list prepend)</SelectItem>
                  <SelectItem value="RPUSH">RPUSH (list append)</SelectItem>
                  <SelectItem value="PUBLISH">PUBLISH (pub/sub)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-field">
              <Label>TTL (seconds)</Label>
              <Input
                type="number"
                value={config.ttl || ''}
                onChange={(e) => handleChange('ttl', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="3600 (1 hour)"
                className="config-input"
              />
            </div>
          </>
        );

      case 'supabaseSink':
        return (
          <>
            <div className="config-section-header">Connection</div>
            <div className="config-field">
              <Label>Credential Source</Label>
              <Select value={config.credentialSource || 'manual'} onValueChange={(v) => handleChange('credentialSource', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Entry</SelectItem>
                  <SelectItem value="env">Environment Variables</SelectItem>
                </SelectContent>
              </Select>
              <span className="config-hint">Use env vars: SUPABASE_URL, SUPABASE_ANON_KEY</span>
            </div>
            {(config.credentialSource || 'manual') === 'manual' && (
              <>
                <div className="config-field">
                  <Label>Project URL</Label>
                  <Input
                    value={config.supabaseUrl || ''}
                    onChange={(e) => handleChange('supabaseUrl', e.target.value)}
                    placeholder="https://xxxxx.supabase.co"
                    className="config-input"
                    data-testid="config-supabase-url"
                  />
                  <span className="config-hint">Your Supabase project URL</span>
                </div>
                <div className="config-field">
                  <Label>API Key (anon/service)</Label>
                  <Input
                    type="password"
                    value={config.supabaseKey || ''}
                    onChange={(e) => handleChange('supabaseKey', e.target.value)}
                    placeholder="eyJhbGciOiJIUzI1NiIs..."
                    className="config-input"
                    data-testid="config-supabase-key"
                  />
                  <span className="config-hint">Anon key for public, service key for full access</span>
                </div>
              </>
            )}
            {config.credentialSource === 'env' && (
              <div className="config-info-box">
                <p>Using environment variables:</p>
                <p className="text-xs text-zinc-500 mt-1">
                  SUPABASE_URL, SUPABASE_ANON_KEY
                </p>
              </div>
            )}
            <div className="config-divider" />
            <div className="config-section-header">Data Settings</div>
            <div className="config-field">
              <Label>Table Name</Label>
              <Input
                value={config.table || ''}
                onChange={(e) => handleChange('table', e.target.value)}
                placeholder="mqtt_messages"
                className="config-input"
                data-testid="config-supabase-table"
              />
            </div>
            <div className="config-field">
              <Label>Operation</Label>
              <Select value={config.operation || 'insert'} onValueChange={(v) => handleChange('operation', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="insert">INSERT</SelectItem>
                  <SelectItem value="upsert">UPSERT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-field">
              <Label>Column Mappings</Label>
              <Textarea
                value={config.columnMappings || ''}
                onChange={(e) => handleChange('columnMappings', e.target.value)}
                placeholder="payload.value:sensor_value&#10;topic:source_topic&#10;timestamp:created_at"
                className="config-input config-textarea"
              />
              <span className="config-hint">Format: source_path:column_name (one per line). Leave empty to insert full payload.</span>
            </div>
          </>
        );

      case 'mongoSink':
        return (
          <>
            <div className="config-section-header">Connection</div>
            <div className="config-field">
              <Label>Connection String</Label>
              <Input
                type="password"
                value={config.connectionString || ''}
                onChange={(e) => handleChange('connectionString', e.target.value)}
                placeholder="mongodb+srv://user:pass@cluster.mongodb.net/db"
                className="config-input"
                data-testid="config-mongo-connection"
              />
              <span className="config-hint">MongoDB connection URI (supports Atlas, self-hosted)</span>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Data Settings</div>
            <div className="config-field">
              <Label>Database</Label>
              <Input
                value={config.database || ''}
                onChange={(e) => handleChange('database', e.target.value)}
                placeholder="mydb"
                className="config-input"
                data-testid="config-mongo-db"
              />
            </div>
            <div className="config-field">
              <Label>Collection</Label>
              <Input
                value={config.collection || ''}
                onChange={(e) => handleChange('collection', e.target.value)}
                placeholder="messages"
                className="config-input"
                data-testid="config-mongo-collection"
              />
            </div>
            <div className="config-field">
              <Label>Operation</Label>
              <Select value={config.operation || 'insertOne'} onValueChange={(v) => handleChange('operation', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="insertOne">Insert One</SelectItem>
                  <SelectItem value="insertMany">Insert Many (batch)</SelectItem>
                  <SelectItem value="updateOne">Update One (upsert)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case 'postgresSink':
        return (
          <>
            <div className="config-section-header">Connection</div>
            <div className="config-field">
              <Label>Connection String</Label>
              <Input
                type="password"
                value={config.connectionString || ''}
                onChange={(e) => handleChange('connectionString', e.target.value)}
                placeholder="postgresql://user:pass@host:5432/database"
                className="config-input"
                data-testid="config-postgres-connection"
              />
              <span className="config-hint">PostgreSQL connection URI</span>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Data Settings</div>
            <div className="config-field">
              <Label>Table Name</Label>
              <Input
                value={config.table || ''}
                onChange={(e) => handleChange('table', e.target.value)}
                placeholder="messages"
                className="config-input"
                data-testid="config-postgres-table"
              />
            </div>
            <div className="config-field">
              <Label>Column Mappings</Label>
              <Textarea
                value={config.columnMappings || ''}
                onChange={(e) => handleChange('columnMappings', e.target.value)}
                placeholder="payload.value:sensor_value&#10;topic:source_topic&#10;timestamp:created_at"
                className="config-input config-textarea"
              />
              <span className="config-hint">Format: source_path:column_name (one per line)</span>
            </div>
            <div className="config-field-row">
              <Label>Create table if not exists</Label>
              <Switch 
                checked={config.autoCreateTable || false} 
                onCheckedChange={(v) => handleChange('autoCreateTable', v)} 
              />
            </div>
          </>
        );

      case 'mysqlSink':
        return (
          <>
            <div className="config-section-header">Connection</div>
            <div className="config-field">
              <Label>Host</Label>
              <Input
                value={config.host || ''}
                onChange={(e) => handleChange('host', e.target.value)}
                placeholder="localhost"
                className="config-input"
                data-testid="config-mysql-host"
              />
            </div>
            <div className="config-field">
              <Label>Port</Label>
              <Input
                type="number"
                value={config.port || 3306}
                onChange={(e) => handleChange('port', parseInt(e.target.value) || 3306)}
                className="config-input"
              />
            </div>
            <div className="config-field">
              <Label>Username</Label>
              <Input
                value={config.username || ''}
                onChange={(e) => handleChange('username', e.target.value)}
                placeholder="root"
                className="config-input"
                data-testid="config-mysql-user"
              />
            </div>
            <div className="config-field">
              <Label>Password</Label>
              <Input
                type="password"
                value={config.password || ''}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="password"
                className="config-input"
                data-testid="config-mysql-pass"
              />
            </div>
            <div className="config-field">
              <Label>Database</Label>
              <Input
                value={config.database || ''}
                onChange={(e) => handleChange('database', e.target.value)}
                placeholder="mydb"
                className="config-input"
                data-testid="config-mysql-db"
              />
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Data Settings</div>
            <div className="config-field">
              <Label>Table Name</Label>
              <Input
                value={config.table || ''}
                onChange={(e) => handleChange('table', e.target.value)}
                placeholder="messages"
                className="config-input"
                data-testid="config-mysql-table"
              />
            </div>
            <div className="config-field">
              <Label>Column Mappings</Label>
              <Textarea
                value={config.columnMappings || ''}
                onChange={(e) => handleChange('columnMappings', e.target.value)}
                placeholder="payload.value:sensor_value&#10;topic:source_topic"
                className="config-input config-textarea"
              />
              <span className="config-hint">Format: source_path:column_name (one per line)</span>
            </div>
          </>
        );

      case 'natsSink':
        return (
          <>
            <div className="config-section-header">Connection</div>
            <div className="config-field">
              <Label>NATS Server URL</Label>
              <Input
                value={config.serverUrl || ''}
                onChange={(e) => handleChange('serverUrl', e.target.value)}
                placeholder="nats://localhost:4222"
                className="config-input"
                data-testid="config-nats-url"
              />
              <span className="config-hint">NATS server URL (supports nats://, tls://)</span>
            </div>
            <div className="config-field">
              <Label>Authentication</Label>
              <Select value={config.authType || 'none'} onValueChange={(v) => handleChange('authType', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="userpass">Username/Password</SelectItem>
                  <SelectItem value="token">Token</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {config.authType === 'userpass' && (
              <>
                <div className="config-field">
                  <Label>Username</Label>
                  <Input
                    value={config.username || ''}
                    onChange={(e) => handleChange('username', e.target.value)}
                    className="config-input"
                  />
                </div>
                <div className="config-field">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={config.password || ''}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="config-input"
                  />
                </div>
              </>
            )}
            {config.authType === 'token' && (
              <div className="config-field">
                <Label>Token</Label>
                <Input
                  type="password"
                  value={config.token || ''}
                  onChange={(e) => handleChange('token', e.target.value)}
                  className="config-input"
                />
              </div>
            )}
            <div className="config-divider" />
            <div className="config-section-header">Publish Settings</div>
            <div className="config-field">
              <Label>Subject</Label>
              <Input
                value={config.subject || ''}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder="sensors.temperature"
                className="config-input"
                data-testid="config-nats-subject"
              />
              <span className="config-hint">NATS subject to publish to</span>
            </div>
          </>
        );

      case 'qstashPublish':
        return (
          <>
            <div className="config-section-header">Destination</div>
            <div className="config-field">
              <Label>Callback URL</Label>
              <Input
                value={config.callbackUrl || ''}
                onChange={(e) => handleChange('callbackUrl', e.target.value)}
                placeholder="https://api.example.com/webhook"
                className="config-input"
                data-testid="config-qstash-url"
              />
              <span className="config-hint">URL to receive the message</span>
            </div>
            <div className="config-field">
              <Label>Queue Name (optional)</Label>
              <Input
                value={config.queueName || ''}
                onChange={(e) => handleChange('queueName', e.target.value)}
                placeholder="mqtt-processing"
                className="config-input"
                data-testid="config-qstash-queue"
              />
              <span className="config-hint">Named queue for ordered delivery</span>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Options</div>
            <div className="config-field">
              <Label>Delay (seconds)</Label>
              <Input
                type="number"
                value={config.delaySeconds || 0}
                onChange={(e) => handleChange('delaySeconds', parseInt(e.target.value) || 0)}
                placeholder="0"
                className="config-input"
              />
              <span className="config-hint">Delay before delivery (0 = immediate)</span>
            </div>
            <div className="config-field">
              <Label>Max Retries</Label>
              <Input
                type="number"
                value={config.retries || 3}
                onChange={(e) => handleChange('retries', parseInt(e.target.value) || 3)}
                min="0"
                max="10"
                className="config-input"
              />
            </div>
          </>
        );

      case 'qstashSchedule':
        return (
          <>
            <div className="config-section-header">Schedule</div>
            <div className="config-field">
              <Label>Callback URL</Label>
              <Input
                value={config.callbackUrl || ''}
                onChange={(e) => handleChange('callbackUrl', e.target.value)}
                placeholder="https://api.example.com/webhook"
                className="config-input"
                data-testid="config-qstash-schedule-url"
              />
            </div>
            <div className="config-field">
              <Label>Cron Expression</Label>
              <Input
                value={config.cron || ''}
                onChange={(e) => handleChange('cron', e.target.value)}
                placeholder="0 * * * *"
                className="config-input"
                data-testid="config-qstash-cron"
              />
              <span className="config-hint">e.g., "0 * * * *" = every hour</span>
            </div>
            <div className="config-field">
              <Label>Common Schedules</Label>
              <div className="config-examples">
                <code onClick={() => handleChange('cron', '* * * * *')}>Every minute</code>
                <code onClick={() => handleChange('cron', '0 * * * *')}>Every hour</code>
                <code onClick={() => handleChange('cron', '0 0 * * *')}>Daily at midnight</code>
                <code onClick={() => handleChange('cron', '0 9 * * 1-5')}>Weekdays 9 AM</code>
              </div>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Payload</div>
            <div className="config-field">
              <Label>JSON Body</Label>
              <Textarea
                value={config.scheduleBody || '{}'}
                onChange={(e) => handleChange('scheduleBody', e.target.value)}
                placeholder='{"event": "scheduled_task", "data": {}}'
                className="config-input config-textarea"
              />
            </div>
          </>
        );

      case 'aggregate':
        return (
          <>
            <div className="config-section-header">Aggregation Settings</div>
            <div className="config-field">
              <Label>Value Field</Label>
              <Input
                value={config.valueField || ''}
                onChange={(e) => handleChange('valueField', e.target.value)}
                placeholder="payload.temperature"
                className="config-input"
                data-testid="config-aggregate-field"
              />
              <span className="config-hint">Field path to aggregate (e.g., payload.value)</span>
            </div>
            <div className="config-field">
              <Label>Operations</Label>
              <div className="config-checkbox-group">
                {['avg', 'sum', 'min', 'max', 'count', 'first', 'last'].map(op => (
                  <label key={op} className="config-checkbox-label">
                    <input
                      type="checkbox"
                      checked={(config.operations || ['avg']).includes(op)}
                      onChange={(e) => {
                        const ops = config.operations || ['avg'];
                        if (e.target.checked) {
                          handleChange('operations', [...ops, op]);
                        } else {
                          handleChange('operations', ops.filter(o => o !== op));
                        }
                      }}
                    />
                    <span>{op.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="config-divider" />
            <div className="config-section-header">Window</div>
            <div className="config-field">
              <Label>Window Size</Label>
              <Input
                type="number"
                value={config.windowSize || 60}
                onChange={(e) => handleChange('windowSize', parseInt(e.target.value) || 60)}
                className="config-input"
              />
            </div>
            <div className="config-field">
              <Label>Window Unit</Label>
              <Select value={config.windowUnit || 'seconds'} onValueChange={(v) => handleChange('windowUnit', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seconds">Seconds</SelectItem>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-field-row">
              <Label>Emit on each message</Label>
              <Switch 
                checked={config.emitOnEach || false} 
                onCheckedChange={(v) => handleChange('emitOnEach', v)} 
              />
            </div>
          </>
        );

      case 'window':
        return (
          <>
            <div className="config-section-header">Time Window</div>
            <div className="config-field">
              <Label>Window Duration</Label>
              <Input
                type="number"
                value={config.duration || 60}
                onChange={(e) => handleChange('duration', parseInt(e.target.value) || 60)}
                className="config-input"
                data-testid="config-window-duration"
              />
            </div>
            <div className="config-field">
              <Label>Duration Unit</Label>
              <Select value={config.durationUnit || 'seconds'} onValueChange={(v) => handleChange('durationUnit', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seconds">Seconds</SelectItem>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-field">
              <Label>Window Type</Label>
              <Select value={config.windowType || 'tumbling'} onValueChange={(v) => handleChange('windowType', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tumbling">Tumbling (non-overlapping)</SelectItem>
                  <SelectItem value="sliding">Sliding (overlapping)</SelectItem>
                  <SelectItem value="session">Session (activity-based)</SelectItem>
                </SelectContent>
              </Select>
              <span className="config-hint">Tumbling: fixed intervals. Sliding: overlapping. Session: activity gaps.</span>
            </div>
            <div className="config-field">
              <Label>Output Format</Label>
              <Select value={config.outputFormat || 'array'} onValueChange={(v) => handleChange('outputFormat', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="array">Array of messages</SelectItem>
                  <SelectItem value="batch">Batch object with metadata</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case 'webhook':
        return (
          <>
            <div className="config-field">
              <Label>URL</Label>
              <Input
                value={config.url || ''}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://api.example.com/webhook"
                className="config-input"
              />
            </div>
            <div className="config-field">
              <Label>Method</Label>
              <Select value={config.method || 'POST'} onValueChange={(v) => handleChange('method', v)}>
                <SelectTrigger className="config-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="config-field">
              <Label>Headers (JSON)</Label>
              <Textarea
                value={config.headers || ''}
                onChange={(e) => handleChange('headers', e.target.value)}
                placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                className="config-input config-textarea"
              />
            </div>
          </>
        );

      case 'kGateway':
        const handleCopyField = (field, value) => {
          navigator.clipboard.writeText(value || '');
          setCopied(field);
          setTimeout(() => setCopied(null), 2000);
        };

        const handleSaveGateway = async () => {
          setSaving(true);
          try {
            const gatewayData = {
              id: config.gatewayId || undefined,
              name: config.name || 'Gateway',
              mac_address: config.mac_address || null,
              gateway_mac_hex: config.mac_address ? config.mac_address.replace(/:/g, '').toUpperCase() : null,
              status: findAccountGateway(gateways, {
                gatewayId: config.gatewayId,
                macAddress: config.mac_address,
              })?.status || config.status || 'offline',
              ip_address: config.ip_address || null,
              // MQTT Settings
              mqtt_broker_host: config.mqtt_broker_host || 'tbmq.chillverify.com',
              mqtt_broker_port: parseInt(config.mqtt_broker_port) || 1883,
              mqtt_username: config.mqtt_username || null,
              mqtt_password: config.mqtt_password || null,
              mqtt_client_id: config.mqtt_client_id || null,
              mqtt_topic: config.mqtt_topic || null,
              // TBMQ Settings
              tbmq_username: config.tbmq_username || null,
              tbmq_password: config.tbmq_password || null,
              // HTTP Settings
              http_endpoint: config.http_endpoint || null,
              http_api_key: config.http_api_key || null,
              http_api_key_name: config.http_api_key_name || 'Authorization',
              http_username: config.http_username || null,
              http_password: config.http_password || null,
              http_auth_mode: config.http_auth_mode || 'None',
              // Metadata
              metadata: config.metadata || null,
            };

            const { data, error } = await saveGateway(gatewayData);
            if (error) {
              toast.error(`Failed to save: ${error.message}`);
            } else {
              toast.success('Gateway saved successfully');
              // Update local config with the saved data
              handleChange('gatewayId', data.id);
              if (onUpdate) {
                onUpdate(node.id, { ...config, gatewayId: data.id, ...data });
              }
            }
          } catch (err) {
            toast.error('Failed to save gateway');
          }
          setSaving(false);
        };

        const handleDeleteGateway = async () => {
          if (!config.gatewayId) {
            // Just delete the node if it's not saved to Supabase
            if (onDelete) onDelete(node.id);
            onClose();
            return;
          }
          
          setSaving(true);
          try {
            const { error } = await removeGateway(config.gatewayId);
            if (error) {
              toast.error(`Failed to delete: ${error.message}`);
            } else {
              toast.success('Gateway deleted');
              if (onDelete) onDelete(node.id);
              onClose();
            }
          } catch (err) {
            toast.error('Failed to delete gateway');
          }
          setSaving(false);
        };

        // Derive the gateway's MQTT topic from its MAC so we can filter live data.
        // Topic format: kbeacon/publish/{CLEANMAC} (see gatewayService.generateMQTTCredentials).
        const cleanMac = (config.mac_address || '').replace(/:/g, '').toUpperCase();
        const gatewayTopic = config.mqtt_topic || (cleanMac ? `kbeacon/publish/${cleanMac}` : '');

        // Filter the live WebSocket stream to messages from THIS gateway.
        const gatewayLiveMessages = (liveMessages || []).filter((msg) => {
          if (!msg || !msg.topic) return false;
          // Match on the gateway's clean MAC appearing in the topic, or an exact topic match.
          const t = String(msg.topic);
          return cleanMac ? t.includes(cleanMac) : (gatewayTopic && t === gatewayTopic);
        });

        // Selecting an existing gateway auto-fills all node fields from the DB row.
        const handleSelectGateway = (gwId) => {
          const gw = gateways.find((g) => String(g.id) === String(gwId));
          if (!gw) return;
          const newConfig = {
            ...config,
            nodeType: 'kGateway',
            gatewayId: gw.id,
            id: gw.id,
            name: gw.name,
            mac_address: gw.mac_address,
            status: gw.status,
            ip_address: gw.ip_address,
            mqtt_broker_host: gw.mqtt_broker_host,
            mqtt_broker_port: gw.mqtt_broker_port,
            mqtt_username: gw.mqtt_username,
            mqtt_password: gw.mqtt_password,
            mqtt_client_id: gw.mqtt_client_id,
            mqtt_topic: gw.mqtt_topic,
            tbmq_username: gw.tbmq_username,
            tbmq_password: gw.tbmq_password,
          };
          setConfig(newConfig);
          if (onUpdate) {
            onUpdate(node.id, newConfig, { silent: true });
          }
        };

        const linkedGateway = findAccountGateway(gateways, {
          gatewayId: config.gatewayId,
          macAddress: config.mac_address,
        });
        const selectedGatewayId = linkedGateway?.id
          ? String(linkedGateway.id)
          : (config.gatewayId ? String(config.gatewayId) : undefined);
        const gatewayStatus = normalizeGatewayStatus(linkedGateway?.status || config.status);

        return (
          <>
            {/* Gateway selector — pick from existing gateways on the account */}
            {gateways && gateways.length > 0 && (
              <div className="config-field" style={{ marginBottom: '12px' }}>
                <Label>Select Existing Gateway</Label>
                <Select
                  value={selectedGatewayId}
                  onValueChange={handleSelectGateway}
                >
                  <SelectTrigger className="config-input" data-testid="config-gateway-select">
                    <SelectValue placeholder="— Choose a gateway —" />
                  </SelectTrigger>
                  <SelectContent className="etl-radix-select-content">
                    {gateways.map((g) => (
                      <SelectItem key={g.id} value={String(g.id)}>
                        {g.name}{g.mac_address ? ` (${g.mac_address})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="config-hint">
                  {config.gatewayId
                    ? 'Editing an existing gateway. Changes below save to your account.'
                    : 'Select to auto-fill all fields from your account.'}
                </span>
              </div>
            )}

            <Tabs defaultValue="general" className="gateway-tabs">
              <TabsList className="gateway-tabs-list">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="mqtt">MQTT</TabsTrigger>
                <TabsTrigger value="http">HTTP</TabsTrigger>
                <TabsTrigger value="data">Data</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="gateway-tab-content">
                <div className="config-section-header">
                  <LuServer className="inline w-4 h-4 mr-2" />
                  Gateway Info
                </div>
                
                <div className="config-field">
                  <Label>Gateway Name</Label>
                  <Input
                    value={config.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="My BLE Gateway"
                    className="config-input"
                    data-testid="config-gateway-name"
                  />
                </div>

                <div className="config-field">
                  <Label>MAC Address</Label>
                  <div className="config-row">
                    <Input
                      value={config.mac_address || ''}
                      onChange={(e) => handleChange('mac_address', e.target.value)}
                      placeholder="AA:BB:CC:DD:EE:FF"
                      className="config-input flex-1"
                      data-testid="config-mac-address"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCopyField('mac', config.mac_address)}
                      className="shrink-0"
                    >
                      {copied === 'mac' ? <LuCheck className="w-4 h-4 text-green-500" /> : <LuCopy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="config-field">
                  <Label>IP Address</Label>
                  <Input
                    value={config.ip_address || ''}
                    onChange={(e) => handleChange('ip_address', e.target.value)}
                    placeholder="192.168.1.100"
                    className="config-input"
                    data-testid="config-ip-address"
                  />
                </div>

                <div className="config-field">
                  <Label>Status</Label>
                  <Input
                    value={gatewayStatusLabel(gatewayStatus)}
                    readOnly
                    className={`config-input gateway-status-readonly gateway-status-readonly--${gatewayStatus}`}
                    data-testid="config-status"
                  />
                  <span className="config-hint">
                    Based on recent gateway data (same as Gateway List).
                  </span>
                </div>

                {config.gatewayId && (
                  <div className="config-field">
                    <Label>Gateway ID</Label>
                    <div className="config-row">
                      <Input
                        value={config.gatewayId || ''}
                        readOnly
                        className="config-input flex-1 text-xs font-mono opacity-70"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopyField('id', config.gatewayId)}
                        className="shrink-0"
                      >
                        {copied === 'id' ? <LuCheck className="w-4 h-4 text-green-500" /> : <LuCopy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="mqtt" className="gateway-tab-content">
                <div className="config-section-header">
                  <LuWifi className="inline w-4 h-4 mr-2" />
                  MQTT Broker
                </div>

                <div className="config-field">
                  <Label>Broker Host</Label>
                  <Input
                    value={config.mqtt_broker_host || 'tbmq.chillverify.com'}
                    onChange={(e) => handleChange('mqtt_broker_host', e.target.value)}
                    placeholder="tbmq.chillverify.com"
                    className="config-input"
                    data-testid="config-mqtt-host"
                  />
                </div>

                <div className="config-field">
                  <Label>Broker Port</Label>
                  <Input
                    type="number"
                    value={config.mqtt_broker_port || 1883}
                    onChange={(e) => handleChange('mqtt_broker_port', parseInt(e.target.value))}
                    className="config-input"
                    data-testid="config-mqtt-port"
                  />
                </div>

                <div className="config-divider" />
                <div className="config-section-header">
                  <LuKey className="inline w-4 h-4 mr-2" />
                  MQTT Credentials
                </div>

                <div className="config-field">
                  <Label>Username</Label>
                  <div className="config-row">
                    <Input
                      value={config.mqtt_username || ''}
                      onChange={(e) => handleChange('mqtt_username', e.target.value)}
                      placeholder="mqtt_username"
                      className="config-input flex-1"
                      data-testid="config-mqtt-username"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCopyField('mqtt_user', config.mqtt_username)}
                      className="shrink-0"
                    >
                      {copied === 'mqtt_user' ? <LuCheck className="w-4 h-4 text-green-500" /> : <LuCopy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="config-field">
                  <Label>Password</Label>
                  <div className="config-row">
                    <Input
                      type="password"
                      value={config.mqtt_password || ''}
                      onChange={(e) => handleChange('mqtt_password', e.target.value)}
                      placeholder="••••••••"
                      className="config-input flex-1"
                      data-testid="config-mqtt-password"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCopyField('mqtt_pass', config.mqtt_password)}
                      className="shrink-0"
                    >
                      {copied === 'mqtt_pass' ? <LuCheck className="w-4 h-4 text-green-500" /> : <LuCopy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="config-field">
                  <Label>Client ID</Label>
                  <Input
                    value={config.mqtt_client_id || ''}
                    onChange={(e) => handleChange('mqtt_client_id', e.target.value)}
                    placeholder="auto-generated"
                    className="config-input"
                    data-testid="config-mqtt-client-id"
                  />
                </div>

                <div className="config-field">
                  <Label>Topic</Label>
                  <Input
                    value={config.mqtt_topic || ''}
                    onChange={(e) => handleChange('mqtt_topic', e.target.value)}
                    placeholder="gw/{mac}/data"
                    className="config-input"
                    data-testid="config-mqtt-topic"
                  />
                  <span className="config-hint">Topic to subscribe for gateway data</span>
                </div>

                <div className="config-divider" />
                <div className="config-section-header">TBMQ Credentials (Optional)</div>

                <div className="config-field">
                  <Label>TBMQ Username</Label>
                  <Input
                    value={config.tbmq_username || ''}
                    onChange={(e) => handleChange('tbmq_username', e.target.value)}
                    placeholder="tbmq_username"
                    className="config-input"
                  />
                </div>

                <div className="config-field">
                  <Label>TBMQ Password</Label>
                  <Input
                    type="password"
                    value={config.tbmq_password || ''}
                    onChange={(e) => handleChange('tbmq_password', e.target.value)}
                    placeholder="••••••••"
                    className="config-input"
                  />
                </div>
              </TabsContent>

              <TabsContent value="http" className="gateway-tab-content">
                <div className="config-section-header">
                  <LuGlobe className="inline w-4 h-4 mr-2" />
                  HTTP API
                </div>

                <div className="config-field">
                  <Label>HTTP Endpoint</Label>
                  <Input
                    value={config.http_endpoint || ''}
                    onChange={(e) => handleChange('http_endpoint', e.target.value)}
                    placeholder="https://api.example.com/gateway"
                    className="config-input"
                    data-testid="config-http-endpoint"
                  />
                </div>

                <div className="config-field">
                  <Label>Auth Mode</Label>
                  <Select 
                    value={config.http_auth_mode || 'None'} 
                    onValueChange={(v) => handleChange('http_auth_mode', v)}
                  >
                    <SelectTrigger className="config-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="None">None</SelectItem>
                      <SelectItem value="Basic">Basic Auth</SelectItem>
                      <SelectItem value="Bearer">Bearer Token</SelectItem>
                      <SelectItem value="ApiKey">API Key</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {config.http_auth_mode === 'Basic' && (
                  <>
                    <div className="config-field">
                      <Label>HTTP Username</Label>
                      <Input
                        value={config.http_username || ''}
                        onChange={(e) => handleChange('http_username', e.target.value)}
                        placeholder="username"
                        className="config-input"
                      />
                    </div>
                    <div className="config-field">
                      <Label>HTTP Password</Label>
                      <Input
                        type="password"
                        value={config.http_password || ''}
                        onChange={(e) => handleChange('http_password', e.target.value)}
                        placeholder="••••••••"
                        className="config-input"
                      />
                    </div>
                  </>
                )}

                {(config.http_auth_mode === 'Bearer' || config.http_auth_mode === 'ApiKey') && (
                  <>
                    <div className="config-field">
                      <Label>API Key Header Name</Label>
                      <Input
                        value={config.http_api_key_name || 'Authorization'}
                        onChange={(e) => handleChange('http_api_key_name', e.target.value)}
                        placeholder="Authorization"
                        className="config-input"
                      />
                    </div>
                    <div className="config-field">
                      <Label>API Key</Label>
                      <div className="config-row">
                        <Input
                          value={config.http_api_key || ''}
                          onChange={(e) => handleChange('http_api_key', e.target.value)}
                          placeholder="your-api-key"
                          className="config-input flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleCopyField('http_key', config.http_api_key)}
                          className="shrink-0"
                        >
                          {copied === 'http_key' ? <LuCheck className="w-4 h-4 text-green-500" /> : <LuCopy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Data tab — live MQTT payloads coming from this gateway */}
              <TabsContent value="data" className="gateway-tab-content">
                <div className="config-section-header">
                  <LuRadio className="inline w-4 h-4 mr-2" />
                  Live Data
                </div>

                {gatewayTopic && (
                  <div className="config-field">
                    <Label>MQTT Topic</Label>
                    <Input
                      value={gatewayTopic}
                      readOnly
                      className="config-input font-mono text-xs opacity-80"
                    />
                  </div>
                )}

                {!mqttConnected ? (
                  <div className="config-field">
                    <div style={{
                      padding: '24px 16px',
                      textAlign: 'center',
                      border: '1px dashed var(--config-border, #e0e0e0)',
                      borderRadius: '8px',
                      color: 'var(--config-text-secondary, #6f6f6f)',
                    }}>
                      <LuWifiOff className="w-6 h-6 mx-auto mb-2 opacity-50" />
                      <p style={{ margin: 0, fontSize: '0.875rem' }}>
                        Connect the MQTT broker to see live data from this gateway.
                      </p>
                      <p style={{ margin: '8px 0 0', fontSize: '0.75rem', opacity: 0.7 }}>
                        {gatewayLiveMessages.length} message(s) buffered.
                      </p>
                    </div>
                  </div>
                ) : gatewayLiveMessages.length === 0 ? (
                  <div className="config-field">
                    <div style={{
                      padding: '24px 16px',
                      textAlign: 'center',
                      border: '1px dashed var(--config-border, #e0e0e0)',
                      borderRadius: '8px',
                      color: 'var(--config-text-secondary, #6f6f6f)',
                    }}>
                      <LuRadio className="w-6 h-6 mx-auto mb-2 opacity-50" />
                      <p style={{ margin: 0, fontSize: '0.875rem' }}>
                        Waiting for data on <code>{gatewayTopic || 'this gateway'}</code>…
                      </p>
                      <p style={{ margin: '8px 0 0', fontSize: '0.75rem', opacity: 0.7 }}>
                        Ensure the gateway is publishing. Recent messages appear here automatically.
                      </p>
                    </div>
                  </div>
                ) : (
                  <GatewayDataView messages={gatewayLiveMessages} />
                )}
              </TabsContent>
            </Tabs>

            <div className="config-footer gateway-footer">
              <CarbonButton
                kind="primary"
                onClick={handleSaveGateway}
                className="config-footer-btn"
                disabled={saving}
                renderIcon={saving ? undefined : Save}
                data-testid="config-save-gateway"
              >
                {saving ? 'Saving...' : 'Save to Supabase'}
              </CarbonButton>
              <CarbonButton
                kind="danger--tertiary"
                onClick={handleDeleteGateway}
                className="config-footer-btn config-footer-btn--danger"
                disabled={saving}
                renderIcon={TrashCan}
                data-testid="config-delete-gateway"
              >
                Delete
              </CarbonButton>
            </div>
          </>
        );

      case 'viewNode': {
        const { gatewayNode, messages: viewMessages, label: upstreamLabel } = getUpstreamGatewayMessages(
          node.id,
          nodes,
          edges,
          liveMessages,
        );
        const upstreamNode = gatewayNode;
        const displayLabel = upstreamLabel || 'gateway';

        return (
          <>
            <div className="config-section-header">
              <LuTable2 className="inline w-4 h-4 mr-2" />
              Data View
            </div>

            <div className="config-field">
              <Label>Connected Source</Label>
              <Input
                value={upstreamNode ? displayLabel : 'Not connected'}
                readOnly
                className="config-input opacity-80"
              />
              <span className="config-hint">
                {upstreamNode
                  ? `Showing live MQTT from "${displayLabel}" (walks upstream to the gateway).`
                  : 'Connect this node to a gateway (directly or via transforms).'}
              </span>
            </div>

            {!mqttConnected ? (
              <div className="config-field">
                <div style={{
                  padding: '24px 16px',
                  textAlign: 'center',
                  border: '1px dashed var(--config-border, #e0e0e0)',
                  borderRadius: '8px',
                  color: 'var(--config-text-secondary, #6f6f6f)',
                }}>
                  <LuWifiOff className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>
                    Click Run to start streaming MQTT data.
                  </p>
                </div>
              </div>
            ) : viewMessages.length === 0 ? (
              <div className="config-field">
                <div style={{
                  padding: '24px 16px',
                  textAlign: 'center',
                  border: '1px dashed var(--config-border, #e0e0e0)',
                  borderRadius: '8px',
                  color: 'var(--config-text-secondary, #6f6f6f)',
                }}>
                  <LuRadio className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>
                    {upstreamNode
                      ? `Waiting for data from "${displayLabel}"…`
                      : 'No data yet. Connect an upstream gateway.'}
                  </p>
                </div>
              </div>
            ) : (
              <GatewayDataView messages={viewMessages} />
            )}
          </>
        );
      }

      default:
        return <p className="config-hint">Select a node to configure its properties</p>;
    }
  };

  return (
    <div className="config-panel" data-testid="config-panel">
      <div className="config-header">
        <div>
          <h3 className="config-title">{config.label || config.title || 'Node'}</h3>
          <span className="config-type">{nodeType}</span>
        </div>
        <CarbonButton
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close configuration"
          onClick={onClose}
          className="config-close-btn"
          data-testid="config-close"
        />
      </div>

      <ScrollArea className="config-scroll">
        <div className="config-body">
          <div className="config-field">
            <Label>Label</Label>
            <Input
              value={config.label || config.title || ''}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Node label"
              className="config-input"
              data-testid="config-label"
            />
          </div>
          
          <div className="config-divider" />

          <AiNodeAssist
            nodeType={nodeType}
            node={node}
            config={aiConfig}
            nodes={nodes}
            edges={edges}
            liveMessages={liveMessages}
            mqttConnected={mqttConnected}
            onApply={handleChange}
          />

          <div className="config-divider" />

          {renderFields()}
        </div>
      </ScrollArea>

      <div className="config-footer">
        {nodeType !== 'kGateway' && (
          <>
            <CarbonButton
              kind="primary"
              onClick={handleSave}
              className="config-footer-btn"
              renderIcon={Save}
              data-testid="config-save"
            >
              Apply Changes
            </CarbonButton>
            <CarbonButton
              kind="danger--tertiary"
              onClick={handleDelete}
              className="config-footer-btn config-footer-btn--danger"
              renderIcon={TrashCan}
              data-testid="config-delete"
            >
              Delete
            </CarbonButton>
          </>
        )}
      </div>
    </div>
  );
};
